import type { IssueKind } from './types';

/**
 * ── WHAT SORT OF WORK A CARD IS, NAMED BY THE BOARD — #302 ──────────────────
 *
 * A card could be a task, a bug, an improvement or a feature, and that list
 * could not be changed. A customer whose work divides differently had to
 * translate their words into ours on every card, and their reports came back in
 * our vocabulary rather than theirs.
 *
 * ⚠️ THE DIFFICULTY IS THAT A TYPE WAS NEVER JUST A WORD. `bug` draws the mark
 * beside the ref, and it is the darker part of every bar in *Where the work
 * is*. Both were attached to the SPELLING — so renaming it to "Defect" would
 * have lost the mark and sent that bar to zero, silently, while every screen
 * carried on looking right. A feature that quietly breaks a customer's
 * reporting the moment they use it is worse than no feature.
 *
 * So the NAME is theirs and the MEANING is ours. Every screen reads the meaning
 * and none of them reads a spelling.
 */

/**
 * What a type COUNTS AS. Three, and the product's — never the customer's.
 *
 * ⛔ NOT EXTENSIBLE, AND THAT IS THE POINT. Each of these is read by code that
 * has to know what to do with it, so a fourth arriving from a settings screen
 * would be a value no chart could draw. Words the PRODUCT defines are a fixed
 * list; words a CUSTOMER defines are rows. `name` above is rows for exactly the
 * opposite reason.
 *
 * THREE RATHER THAN TWO, decided by Vinnie on 31 August 2026. His reporting
 * specification names Feature, Bug, Tech Debt and Maintenance and asks "are we
 * building, or firefighting?" — the question #317 exists to answer. Tech debt
 * and maintenance are neither new nor broken, and forcing them into either
 * side puts a number into the firefighting chart that nobody put there.
 */
export type Means = 'building' | 'fixing' | 'upkeep';

export const MEANS_ORDER: Means[] = ['building', 'fixing', 'upkeep'];

export const MEANS_LABEL: Record<Means, string> = {
  building: 'Building',
  fixing: 'Fixing',
  upkeep: 'Upkeep',
};

/** Said in full where somebody is choosing one, rather than reading a chart. */
export const MEANS_BLURB: Record<Means, string> = {
  building: 'Something that was not there before, or a real improvement to something that was.',
  fixing: 'Something is broken and should not be. These carry the mark on the card.',
  upkeep: 'Neither new nor broken — the work of keeping it running.',
};

export interface IssueType {
  id: string;
  /**
   * WHAT `issues.kind` HOLDS. Never shown and never changed once made.
   *
   * That is what makes renaming free: a card that said `bug` in June still says
   * `bug`, and the board decides `bug` is displayed as "Defect". A rename is an
   * update to one row and touches no issue at all.
   */
  slug: string;
  name: string;
  means: Means;
  position: number;
  /** Set means retired: offered nowhere new, kept everywhere it already is. */
  retiredAt?: number | null;
}

/**
 * WHAT A BOARD MADE TODAY STARTS WITH — Vinnie's own reporting specification.
 *
 * Chosen by him on 31 August over today's four: *"they are the ones you asked
 * reports to be drawn in, and 'task' is a word for work nobody has
 * classified."*
 *
 * ⚠️ EVERY BOARD THAT ALREADY EXISTS KEEPS TASK, BUG, IMPROVEMENT AND FEATURE.
 * This is only about creation. Migration 0072 gives every existing board
 * exactly what it has today, because anything else would silently reclassify
 * work already on it.
 */
export const NEW_BOARD_TYPES: Omit<IssueType, 'id'>[] = [
  { slug: 'feature', name: 'Feature', means: 'building', position: 0 },
  { slug: 'bug', name: 'Bug', means: 'fixing', position: 1 },
  { slug: 'techdebt', name: 'Tech debt', means: 'upkeep', position: 2 },
  { slug: 'maintenance', name: 'Maintenance', means: 'upkeep', position: 3 },
];

/**
 * The four every board had before #302, as a last resort.
 *
 * Read ONLY when a board's types have not arrived yet — the first paint after a
 * sign-in, and a screen check that seeds no types. A card drawn against this
 * looks exactly as it did before this card existed, which is the right thing to
 * show for the half-second before the truth arrives.
 */
export const ORIGINAL_TYPES: IssueType[] = [
  { id: 'o-task', slug: 'task', name: 'Task', means: 'upkeep', position: 0 },
  { id: 'o-bug', slug: 'bug', name: 'Bug', means: 'fixing', position: 1 },
  { id: 'o-improvement', slug: 'improvement', name: 'Improvement', means: 'building', position: 2 },
  { id: 'o-feature', slug: 'feature', name: 'New feature', means: 'building', position: 3 },
];

/** Live ones, in the order the board put them. What every picker offers. */
export const liveTypes = (types: IssueType[]): IssueType[] =>
  types.filter((t) => !t.retiredAt).sort((a, b) => a.position - b.position);

/** Everything, retired last, for a settings screen. */
export const allTypes = (types: IssueType[]): IssueType[] =>
  [...types].sort(
    (a, b) => Number(!!a.retiredAt) - Number(!!b.retiredAt) || a.position - b.position
  );

/**
 * The type a card is, by the slug it holds.
 *
 * ⚠️ RETIRED ONES RESOLVE. A card keeps what it was given — that is the whole
 * of "retired, never deleted", and a card whose type came back undefined would
 * read as having no type at all rather than an old one.
 */
export function typeOf(kind: string, types: IssueType[]): IssueType | undefined {
  return types.find((t) => t.slug === kind);
}

/**
 * What a card counts as.
 *
 * ⚠️ `upkeep` WHEN NOTHING IS KNOWN, and never `fixing`. An unknown type that
 * guessed "fixing" would put work into the firefighting number that nobody
 * classified, which is the one number on that chart somebody makes a decision
 * from. Upkeep is the honest answer for "nobody has said".
 */
export function meansOf(kind: string, types: IssueType[]): Means {
  return typeOf(kind, types)?.means ?? 'upkeep';
}

/** What to write on a chip. Falls back to the slug, which beats saying nothing. */
export function labelOf(kind: string, types: IssueType[]): string {
  return typeOf(kind, types)?.name ?? kind;
}

/**
 * IS THIS LABEL JUST THE CARD'S OWN TYPE, SAID AGAIN? — #335.
 *
 * A card can carry a label spelling the same word as its type, and the two are
 * drawn as near-identical chips a few inches apart. Harmless until a type is
 * renamed, at which point the card shows the new name and the old word side by
 * side and reads as a rename that half-worked.
 *
 * It cost an evening: Vinnie, 2 September 2026, reported *"there is still a tag
 * that says its a bug"* on a card whose type he had renamed to Defect. The type
 * was right. The second chip was a label. Settling that took a fresh board, a
 * rename walked end to end, a hunt through every rendered word on three
 * screens, and a query across 561 issues — of which 5 carried a label matching
 * their own type, all `bug`, all arrived with the cards.
 *
 * ⚠️ MATCHED ON THE SLUG **AND** ON THE CURRENT NAME, because those are the two
 * words that get typed. `bug` is what the old label says; `Defect` is what
 * somebody would type today. Either is the type repeating itself.
 *
 * NOTHING IS DELETED AND NOTHING IS EDITED. The label is still stored, still
 * exported, still filterable, and still in the box on the card where it can be
 * removed. This governs one chip that repeats what is already on screen.
 */
export function echoesTheType(label: string, kind: string, types: IssueType[]): boolean {
  const said = label.trim().toLowerCase();
  if (!said) return false;
  const mine = typeOf(kind, types);
  return said === kind.trim().toLowerCase() || said === (mine?.name ?? '').trim().toLowerCase();
}

/** Does a card carry the mark? Every Fixing type does, whatever it is called. */
export const marked = (kind: string, types: IssueType[]): boolean =>
  meansOf(kind, types) === 'fixing';

/**
 * THE MARK EACH KIND OF WORK WEARS — one source of truth (#302).
 *
 * The card face and the opened card both draw a mark led by what the work
 * MEANS, and they must never disagree: they did, drawing a wrench and a gear
 * for the same Upkeep card — two glyphs for one type (Vinnie, 14 September). So
 * the icon lives here and both read it. Building is the star, Fixing the bug,
 * Upkeep the checkbox (the task mark). Returns an `Icon` name.
 */
export function meansMark(means: Means): 'star' | 'bug' | 'task' {
  return means === 'building' ? 'star' : means === 'fixing' ? 'bug' : 'task';
}

/**
 * ── THE FOUR ORIGINAL WORDS, WHICH NEVER STOP WORKING ───────────────────────
 *
 * `- **Kind:** bug` is written into tracker files by assistants on other
 * projects, against instructions this app hands out. Once a board can rename
 * its types, what does a file written for one board mean on another?
 *
 * Vinnie's answer, 31 August: **the vocabulary never stops being shared.** The
 * four originals become permanent aliases onto whatever that board means by
 * them, so nothing already written can break — on any board, for ever. The
 * board's own names work AS WELL, not instead.
 *
 * Mapped by MEANING rather than by spelling, because a board using his
 * specification's four has no `task` and no `improvement` to match against —
 * but it does have something that counts as upkeep and something that counts as
 * building, which is what those words were being used to say.
 */
const ALIAS: Record<string, Means> = {
  bug: 'fixing',
  task: 'upkeep',
  improvement: 'building',
  feature: 'building',
};

/**
 * What a word in a tracker file means on THIS board, or nothing.
 *
 * ⚠️ THE ORDER MATTERS AND IS NOT ARBITRARY.
 *
 *   1. the slug, because that is the identity and cannot be ambiguous
 *   2. the name the board chose, because that is what its own people write
 *   3. the four originals, by meaning, so no existing file can break
 *
 * The slug wins over the name so that a board which renamed "Bug" to
 * "Regression" and later added a NEW type called "Bug" cannot make the word
 * `bug` in an old file point at the new one. Identity beats display, always.
 *
 * Nothing returned means the push STOPS and says so — never a fallback. A word
 * quietly landing as something else is the whole failure this card is about.
 */
export function resolveType(said: string, types: IssueType[]): IssueType | undefined {
  const want = said.trim().toLowerCase();
  if (!want) return undefined;
  const bySlug = types.find((t) => t.slug.toLowerCase() === want);
  if (bySlug) return bySlug;
  const byName = types.find((t) => t.name.trim().toLowerCase() === want);
  if (byName) return byName;
  const means = ALIAS[want];
  if (!means) return undefined;
  /* The live one the board put first. A retired type is not what somebody
     writing `bug` today means, and offering it would file new work into a
     classification the board has stopped using. */
  return liveTypes(types).find((t) => t.means === means);
}

/** Every word this board accepts, for the instructions and for a refusal. */
export function wordsFor(types: IssueType[]): string[] {
  const live = liveTypes(types);
  return [
    ...new Set([
      ...live.map((t) => t.name),
      /* Always offered, on every board, because they are always accepted. */
      ...Object.keys(ALIAS),
    ]),
  ];
}

/**
 * A slug from a name somebody typed.
 *
 * Made once, at creation, and never again — see `slug` above. Two types whose
 * names reduce to the same slug are separated by a number rather than refused:
 * "Tech debt" and "tech-debt" are a reasonable pair of names to want, and the
 * slug is private, so there is nothing to explain to anybody.
 */
export function slugFor(name: string, taken: string[]): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'type';
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n++) if (!taken.includes(`${base}-${n}`)) return `${base}-${n}`;
}

/** For the few places that still hand around the original union. */
export const isOriginalWord = (k: string): k is IssueKind => k in ALIAS;
